/**
 * PasswordRequirements: the live checklist under a new-password field.
 *
 * 2026-08-27 register row SGN-02. Paul, walking the firm signup screen on
 * 25 August: the password was accepted with no complexity rule at all. The
 * rules themselves live in lib/password-policy.ts; this file only draws them.
 *
 * It ticks as you type rather than turning red on submit, so a person sees what
 * is still owed while they can still act on it. Nothing here is red: an unmet
 * rule is a quiet grey line, because a rule you have not reached yet is not a
 * mistake you have made.
 *
 * Styling is inline and deliberately borrowed from the hint line that already
 * sits under these fields on all three screens (12px, --ink-3, marginTop -2),
 * so it reads as the same furniture on signup, on reset-password and in the
 * change-password panel on /app/security rather than as a new component.
 *
 * Pass the same `id` into the field's aria-describedby so a screen reader hears
 * the outstanding rules while the field has focus.
 */

import { Check, Circle } from 'lucide-react';
import { PASSWORD_RULES } from '../../lib/password-policy';

export function PasswordRequirements({
  value,
  id,
}: {
  /** The live field value. Every rule is evaluated against it on each render. */
  value: string;
  id: string;
}) {
  return (
    <ul
      id={id}
      role="list"
      data-testid="password-requirements"
      style={{
        listStyle: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        margin: 0,
        marginTop: -2,
        padding: 0,
      }}
    >
      {PASSWORD_RULES.map((rule) => {
        const met = rule.isMet(value);
        return (
          <li
            key={rule.id}
            data-rule={rule.id}
            data-met={met ? 'yes' : 'no'}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              lineHeight: 1.45,
              color: met ? 'var(--positive)' : 'var(--ink-3)',
            }}
          >
            {met ? (
              <Check size={13} strokeWidth={2.5} aria-hidden="true" />
            ) : (
              <Circle size={13} strokeWidth={1.6} aria-hidden="true" />
            )}
            <span>{rule.label}</span>
            <span className="sr-only">{met ? ', done' : ', still needed'}</span>
          </li>
        );
      })}
    </ul>
  );
}
